import { type RefObject, useEffect, useReducer, useRef, useState } from 'react';

import { bind } from 'bind-event-listener';
import debounce from 'lodash/debounce';

import { type ScrollDirection } from './types';

const DEBOUNCE_DURATION = 50;
const MIN_SCROLL_OFFSET = 0;

export default function useScrollDirection(ref: RefObject<HTMLDivElement>) {
	const lastScrollY = useRef(0);
	const [scrollDirection, setScrollDirection] = useState<ScrollDirection | undefined>(undefined);
	const [, forceUpdate] = useReducer((x) => x + 1, 0);

	useEffect(() => {
		if (!ref || !ref.current) {
			return;
		}

		const onScroll = debounce(() => {
			const scrollY = ref.current!.scrollTop;
			const direction = scrollY > lastScrollY.current ? 'down' : 'up';

			if (
				scrollY - lastScrollY.current > MIN_SCROLL_OFFSET ||
				scrollY - lastScrollY.current < -1 * MIN_SCROLL_OFFSET
			) {
				// Force an update to ensure the component re-triggers the event
				if (direction === scrollDirection) {
					forceUpdate();
				} else {
					setScrollDirection(direction);
				}
			}

			lastScrollY.current = scrollY > 0 ? scrollY : 0;
		}, DEBOUNCE_DURATION);

		// Bind returns an unbind function, which we can use to clean up the event listener
		// when the component unmounts or ref changes.
		return bind(ref.current, {
			type: 'scroll',
			listener: onScroll,
			options: {
				capture: true,
			},
		});
	}, [ref, scrollDirection]);

	return scrollDirection;
}
